import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Calendar, Briefcase, Building2 } from 'lucide-react';

export default function CVHistory() {
  const { token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cv/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (historyId, docType, filename) => {
    try {
      const response = await axios.get(
        `${API}/cv/history/${historyId}/download?doc_type=${docType}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.data) {
        // Convert base64 to blob and download
        const blob = base64ToBlob(response.data.data, 'application/pdf');
        const url = URL.createObjectURL(blob);
        downloadFile(url, response.data.filename || filename);
        toast.success(`${docType === 'cv' ? 'CV' : 'Cover Letter'} downloaded!`);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const base64ToBlob = (base64, contentType) => {
    const byteCharacters = atob(base64);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: contentType });
  };

  const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
      <div className="container mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate('/cv-builder')} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to CV Builder
        </Button>

        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2">CV & Cover Letter History</h1>
            <p className="text-gray-600">Download your previously generated documents anytime</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <p className="mt-4 text-gray-600">Loading history...</p>
            </div>
          ) : history.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Documents Yet</h3>
              <p className="text-gray-600 mb-6">
                You haven't generated any CVs or Cover Letters yet.
              </p>
              <Button 
                onClick={() => navigate('/cv-builder')}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Generate Your First CV
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {history.map((item) => (
                <Card key={item.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left side - Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-5 h-5 text-indigo-600" />
                          <h3 className="text-xl font-bold text-gray-900">
                            {item.job_title}
                          </h3>
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <Building2 className="w-4 h-4" />
                          <span className="font-medium">{item.company_name}</span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <Calendar className="w-4 h-4" />
                          <span>Generated on {formatDate(item.created_at)}</span>
                        </div>

                        {item.job_description && (
                          <details className="mt-3">
                            <summary className="text-sm text-indigo-600 cursor-pointer hover:underline">
                              View Job Description
                            </summary>
                            <p className="mt-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                              {item.job_description.length > 300 
                                ? item.job_description.substring(0, 300) + '...' 
                                : item.job_description}
                            </p>
                          </details>
                        )}
                      </div>

                      {/* Right side - Download Buttons */}
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleDownload(item.id, 'cv', `${item.job_title}_cv.pdf`)}
                          className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap"
                          size="sm"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download CV
                        </Button>
                        
                        <Button
                          onClick={() => handleDownload(item.id, 'cover_letter', `${item.job_title}_cover_letter.pdf`)}
                          className="bg-purple-600 hover:bg-purple-700 whitespace-nowrap"
                          size="sm"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Cover Letter
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-8 text-center">
              <p className="text-gray-600">
                Total Documents: <span className="font-bold">{history.length}</span> CV{history.length !== 1 ? 's' : ''} and Cover Letter{history.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
